import { html, LitElement, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { initializeIconByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { CSCDeliveryMatchMethodDropoff } from './delivery-match-method-dropoff.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { mapStyle } from './delivery-match-dropoff-method-selector.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { consume } from '@lit/context'
import { DeliveryMatchActionsContext, DeliveryMatchDropoffMethodModeContext, DeliveryMatchDropoffMethodsContext, DeliveryMatchErrorContext, DeliveryMatchGeolocationContext, DeliveryMatchIsSearchingContext, NO_SELECTED_METHOD, type DeliveryMatchActionsContextType, type DeliveryMatchCurrencyGeolocationType, type DeliveryMatchDropoffMethodModeContextType, type DeliveryMatchDropoffMethodsContextType, type DeliveryMatchErrorContextType } from './provider.js'
import type { DropoffMethod } from './api/shipment-methods.js'
import { classMap } from 'lit/directives/class-map.js'
import { CSCDeliveryMatchZipSearch } from './delivery-match-zip-search.js'
import { CSCDeliveryMatchDropoffMethodSelectorMap } from './delivery-match-dropoff-method-selector-map.js'
import type { CSCSegmentedControlChangeEvent } from '@cloudsuite/mosaic-elements'
import { DEFAULT_VISIBLE_METHODS_ON_INIT } from './visible-methods-on-init.js'

@customElement('delivery-match-search-error')
class DeliveryMatchSearchError extends LitElement {
  protected override createRenderRoot(): this { return this }

  @consume({ context: DeliveryMatchErrorContext, subscribe: true })
  private error!: DeliveryMatchErrorContextType

  override render() {
    if (!this.error || this.error.level !== 'search') return nothing

    return html`
      <cse-alert variant="warning">
        ${t('search_for_dropoff_locations_error_title', { ns: 'csc-delivery-match', default: 'We couldn\'t find any locations. Please try again.' })}
      </cse-alert>
    `
  }
}

initializeIconByRole('csc-delivery-match.selected-method', {
  name: 'check',
  style: 'light',
})

@customElement('csc-delivery-match-dropoff-method-selector')
export class CSCDeliveryMatchDropoffMethodSelector extends LitElement {

  static override styles = [hostStyle, mapStyle]

  @consume({ context: DeliveryMatchDropoffMethodsContext, subscribe: true })
  private dropoffMethods!: DeliveryMatchDropoffMethodsContextType

  @consume({ context: DeliveryMatchActionsContext })
  private actions!: DeliveryMatchActionsContextType

  @consume({ context: DeliveryMatchDropoffMethodModeContext, subscribe: true })
  dropoffMethodMode!: DeliveryMatchDropoffMethodModeContextType


  @consume({ context: DeliveryMatchGeolocationContext, subscribe: true })
  private initialCenter!: DeliveryMatchCurrencyGeolocationType

  @consume({ context: DeliveryMatchIsSearchingContext, subscribe: true })
  private isSearching!: boolean


  private showRadioButtonsMethods: boolean
  private visibleMethodsOnInit: number

  private static readonly BATCH_SIZE = 5

  @state()
  private nextBatch: number = CSCDeliveryMatchDropoffMethodSelector.BATCH_SIZE

  private methodElementCache = new Map<string, CSCDeliveryMatchMethodDropoff>()

  constructor({ showRadioButtonsMethods, visibleMethodsOnInit }: { showRadioButtonsMethods?: boolean; visibleMethodsOnInit?: number }) {
    super()
    this.showRadioButtonsMethods = showRadioButtonsMethods ?? false
    this.visibleMethodsOnInit = visibleMethodsOnInit ?? DEFAULT_VISIBLE_METHODS_ON_INIT
  }

  private getMethodElement(method: DropoffMethod): CSCDeliveryMatchMethodDropoff {
    const cached = this.methodElementCache.get(method.methodId)
    if (cached) {
      cached.method = method
      cached.showRadioButton = this.showRadioButtonsMethods
      cached.requestUpdate()
      return cached
    }
    const element = new CSCDeliveryMatchMethodDropoff({ method, showRadioButton: this.showRadioButtonsMethods })
    this.methodElementCache.set(method.methodId, element)
    return element
  }

  private handleModeChange = async (event: CSCSegmentedControlChangeEvent) => {
    const mode = event.value
    this.actions.setDropoffMethodMode(mode as 'list' | 'map')

    if (mode === 'list') {
      this.actions.setSelectedMethod(NO_SELECTED_METHOD)
    }
  }

  private handleVisibleCountChange = (event: CustomEvent<{ visible: number; total: number; nextBatch: number }>) => {
    if (this.nextBatch === event.detail.nextBatch) return
    this.nextBatch = event.detail.nextBatch
  }

  override render() {
    return html`
      <csc-google-maps-map-provider initial-center="${this.initialCenter}">
        <cse-paper class="delivery-match-dropoff-selector">
        <div slot="heading"  class="delivery-match-title">
          <cse-text variant="heading-3">
            ${t('dropoff_methods_title', { ns: 'csc-delivery-match', default: 'Choose a pickup point' })}
          </cse-text>

          <cse-segmented-control @segmented-control-change=${(event: CSCSegmentedControlChangeEvent) => this.handleModeChange(event)}>
            <cse-segmented-control-item value="list" class="list-button">
              <cse-icon icon-role="csc-delivery-match.list"></cse-icon>
              ${t('segment_list_name', { ns: 'csc-delivery-match', default: 'List' })}
            </cse-segmented-control-item>
            <cse-segmented-control-item value="map" class="map-button">
              <cse-icon icon-role="csc-delivery-match.map"></cse-icon>
              ${t('segment_map_name', { ns: 'csc-delivery-match', default: 'Map' })}
            </cse-segmented-control-item>
          </cse-segmented-control>
        </div>
        ${new CSCDeliveryMatchZipSearch()}
        ${new DeliveryMatchSearchError()}
        ${this.renderMethodList()}
        ${new CSCDeliveryMatchDropoffMethodSelectorMap()}
      </cse-paper>
    </csc-google-maps-map-provider>`
  }

  private renderSkeletons() {
    return html`
      <div class="${classMap({ list: true, hidden: this.dropoffMethodMode !== 'list' })}">
        <cse-skeleton height="80" index="0"></cse-skeleton>
        <cse-skeleton height="80" index="1"></cse-skeleton>
        <cse-skeleton height="80" index="2"></cse-skeleton>
      </div>`
  }

  private renderMethodList() {
    if (this.isSearching) return this.renderSkeletons()

    return html`
      <cse-expandable
        variant="list-incremental"
        visible-on-init="${this.visibleMethodsOnInit}"
        batch-size="${CSCDeliveryMatchDropoffMethodSelector.BATCH_SIZE}"
        @visible-count-change=${this.handleVisibleCountChange}
        class="${classMap({
          list: true,
          hidden: this.dropoffMethodMode !== 'list'
        })}"
      >
        <cse-link slot="toggle-open">
          ${t('show_more_dropoff', {
            ns: 'csc-delivery-match',
            default: 'Show {{variable_count}} more',
            variable_count: this.nextBatch,
          })}
        </cse-link>
        <cse-group direction="column">
          ${this.dropoffMethods.map((method) => this.getMethodElement(method))}
        </cse-group>
      </cse-expandable>`
  }
}
